import { CuboidStyle, CuboidVariants } from '@abyss/3d-viewer';
import { CuboidBase, CuboidStyleHandlers, StyledCuboid } from '../types';
import { useCallback } from 'react';

export const makeCuboidStyle = (rgb: number): CuboidStyle => ({
  variant: CuboidVariants.LinesAndTransparentMesh,
  lineColor: rgb,
  lineWidth: 1,
  lineOpacity: 1,
  fillColor: rgb,
  fillOpacity: 0.1,
});

export const makeHoveredCuboidStyle = (rgb: number): CuboidStyle => ({
  variant: CuboidVariants.LinesAndTransparentMesh,
  lineColor: rgb,
  lineWidth: 2,
  lineOpacity: 1,
  fillColor: rgb,
  fillOpacity: 0.35,
});

export const makeCurrentCuboidStyle = (
  rgb: number,
  hidePointsOutsideCuboid: boolean
): CuboidStyle => ({
  variant: CuboidVariants.LinesAndTransparentMesh,
  lineColor: rgb,
  lineWidth: 2,
  lineOpacity: 1.0,
  fillColor: rgb,
  fillOpacity: hidePointsOutsideCuboid ? 0.01 : 0.3,
});

export const useDefaultCuboidStyles = <CuboidT extends CuboidBase>(
  getCuboidColor: (cuboid: CuboidT) => number,
  hidePointsOutsideCuboid: boolean
): CuboidStyleHandlers<CuboidT> => {
  const makeCuboidStyles = useCallback(
    (cuboid: CuboidT) => {
      const color = getCuboidColor(cuboid);
      return {
        style: makeCuboidStyle(color),
        hoveredStyle: makeHoveredCuboidStyle(color),
      };
    },
    [getCuboidColor]
  );

  const makeCurrentCuboidStyleHandler = useCallback(
    (cuboid: StyledCuboid<CuboidT>) => {
      const color = cuboid.hoveredStyle?.lineColor ?? cuboid.style.lineColor;
      return makeCurrentCuboidStyle(color, hidePointsOutsideCuboid);
    },
    [hidePointsOutsideCuboid]
  );

  return {
    makeCuboidStyles,
    makeCurrentCuboidStyle: makeCurrentCuboidStyleHandler,
  };
};
